import React from 'react';
import { Table, Box } from '@legion-ui/core';
import { FaEye, FaEnvelopeOpen } from 'react-icons/fa';
import Button from '../Button';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

export interface EmailBlast {
  id: number | string;
  subject: string;
  recipientCount: number;
  status: 'Sent' | 'Draft' | 'Processed' | 'Failed' | 'sent' | 'draft' | 'processed' | 'failed';
  updateAt: string;
}

interface EmailBlastTableProps {
  emailBlasts: (EmailBlast | undefined)[];
  isLoading: boolean;
  onView: (emailBlast: EmailBlast) => void;
  currentPage?: number;
  itemsPerPage?: number;
}

const EmailBlastTable: React.FC<EmailBlastTableProps> = ({
  emailBlasts = [],
  isLoading = false,
  onView,
  currentPage = 1,
  itemsPerPage = 10,
}) => {
  const tableHeader = ['No', 'Subject', 'Recipient Count', 'Status', 'Update At', 'Action'];

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusIndicator = (status: string) => {
    if (status === 'Sent') {
      return (
        <span className="inline-flex items-center px-3 py-1 text-xs font-normal text-green-600 bg-green-100 rounded-full">
          <span className="w-2 h-2 bg-green-600 rounded-full mr-1"></span>
          {status}
        </span>
      );
    } else if (status === 'Draft') {
      return (
        <span className="inline-flex items-center px-3 py-1 text-xs font-normal text-gray-600 bg-gray-100 rounded-full">
          <span className="w-2 h-2 bg-gray-600 rounded-full mr-1"></span>
          {status}
        </span>
      );
    } else if (status === 'Processed') {
      return (
        <span className="inline-flex items-center px-3 py-1 text-xs font-normal text-blue-600 bg-blue-100 rounded-full">
          <span className="w-2 h-2 bg-blue-600 rounded-full mr-1 animate-pulse"></span>
          {status}
        </span>
      );
    } else if (status === 'Failed') {
      return (
        <span className="inline-flex items-center px-3 py-1 text-xs font-normal text-red-600 bg-red-100 rounded-full">
          <span className="w-2 h-2 bg-red-600 rounded-full mr-1"></span>
          {status}
        </span>
      );
    }
    return status;
  };

  const renderEmailBlasts = () => {
    const hasValidEmailBlast = emailBlasts.some((email) => email && Object.keys(email).length > 0);

    if (!hasValidEmailBlast) {
      return (
        <tr>
          <td colSpan={6} className="text-center">
            <div className="min-h-[33.25rem] flex flex-col justify-center items-center text-gray-500">
              <FaEnvelopeOpen size={124} />
              <p className="mt-2 text-lg">No email blasts found</p>
            </div>
          </td>
        </tr>
      );
    }

    return emailBlasts.map((emailBlast: EmailBlast | undefined, index: number) => {
      if (!emailBlast) return null;

      const rowNumber = (currentPage - 1) * itemsPerPage + index + 1;

      return (
        <tr key={emailBlast.id}>
          <td>{rowNumber}</td>
          <td>{emailBlast.subject}</td>
          <td>{emailBlast.recipientCount.toLocaleString()}</td>
          <td>{getStatusIndicator(emailBlast.status)}</td>
          <td>{formatDate(emailBlast.updateAt)}</td>
          <td>
            <Box width="120px">
              <Button
                iconLeft={<FaEye />}
                variant="outline"
                type="button"
                size="sm"
                onClick={() => onView(emailBlast)}
              >
                View
              </Button>
            </Box>
          </td>
        </tr>
      );
    });
  };

  return (
    <Table hoverable responsive className="!overflow-x-auto">
      <thead className="whitespace-nowrap">
        <tr>
          {tableHeader.map((item, index) => (
            <th key={index}>{item}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          // Skeleton rows
          <>
            {[...Array(5)].map((_, rowIdx) => (
              <tr key={rowIdx}>
                {Array(tableHeader.length)
                  .fill(0)
                  .map((_, colIdx) => (
                    <td key={colIdx}>
                      <Skeleton height={24} />
                    </td>
                  ))}
              </tr>
            ))}
          </>
        ) : (
          renderEmailBlasts()
        )}
      </tbody>
    </Table>
  );
};

export default EmailBlastTable;
